import produce from 'immer';

import {
  DefaultDependencyInfoOptions,
  ProjectType,
  ReportCategories,
  ReportFormats,
} from '../../components/projects/generic/ReportGenerationV2/utils';
import { createReducer, getErrorStatus, getInitialStatus, getPendingStatus } from '../utils';
import type { Report } from '../../../models/report/types';
import type {
  DependencyInfoOption,
  ProjectTypes,
  ReportCategory,
  ReportFormatType,
  ReportOptions,
} from '../../components/projects/generic/shared/types';
import type { Project, ReduxActionResponse, SyncReduxAction } from '../../types';

import { actionTypes } from './actions';
import type { ReportGenerationStore } from './types';

export const initialState: Readonly<ReportGenerationStore> = Object.freeze({
  asyncStatus: {
    fetchPublicReportUrl: getInitialStatus(),
    queueEmailReport: getInitialStatus(),
    queuePublishReport: getInitialStatus(),
    updateCustomText: getInitialStatus(),
    updateDependencyInfoOptions: getInitialStatus(),
  },
  projectType: null,
  /**
   * Which type of report is being generated.
   */
  category: ReportCategories.Licensing,
  /**
   * Export format of the report being generated
   */
  format: ReportFormats.HTML,
  /**
   * Project context
   */
  projectContext: {
    [ProjectType.Project]: {
      revision: '',
    },
    [ProjectType.ReleaseGroup]: {
      groupId: 0,
      releaseId: 0,
    },
  },
  /**
   * Report options
   */
  options: {
    includeLicenseList: true,
    includeConcludedLicenses: false,
    includeProjectLicense: false,
    includeLicenseScan: false,
    includeDirectDependencies: true,
    includeDeepDependencies: true,
    includeLicenseHeaders: false,
    includeDependencySummary: false,
    includeCopyrightList: false,
    includeOpenVulnerabilities: false,
    includeClosedVulnerabilities: false,
    includeFileMatches: false,
    includeSupportStatus: false,
  },
  /**
   * Public report URL
   */
  publicReportUrl: '',
  /**
   * Custom text for report header - provided from project data
   */
  customHeaderText: '',
  /**
   * Dependency Info Options (BOM Columns)
   * TS wont resolve this array to an overlapping type, so we have to cast it
   */
  dependencyInfoOptions: DefaultDependencyInfoOptions,
});

/**
 * Resets non-context state (ie, not category, format, options, etc)
 */
const resetStateReducer = {
  [actionTypes.RESET]: (state: ReportGenerationStore) =>
    produce(state, draftState => {
      draftState.publicReportUrl = initialState.publicReportUrl;
    }),
};

const setProjectTypeReducer = {
  [actionTypes.SET_PROJECT_TYPE]:
  (state: ReportGenerationStore, action: SyncReduxAction<{ projectType: ProjectTypes }>) =>
    produce(state, draftState => {
      draftState.projectType = action.projectType;
    }),
};

const setCategoryReducer = {
  [actionTypes.SET_CATEGORY]:
  (state: ReportGenerationStore, action: SyncReduxAction<{ category: ReportCategory }>) =>
    produce(state, draftState => {
      draftState.category = action.category;
    }),
};

const setFormatReducer = {
  [actionTypes.SET_FORMAT]:
  (state: ReportGenerationStore, action: SyncReduxAction<{ format: ReportFormatType }>) =>
    produce(state, draftState => {
      draftState.format = action.format;
    }),
};

const setProjectContextReducer = {
  [actionTypes.SET_PROJECT_CONTEXT]:
  (state: ReportGenerationStore, action: SyncReduxAction<{ revision: string }>) =>
    produce(state, draftState => {
      draftState.projectType = ProjectType.Project;
      draftState.projectContext[ProjectType.Project].revision = action.revision;
    }),
  [actionTypes.SET_PROJECT_GROUP_CONTEXT]:
  (state: ReportGenerationStore, action:SyncReduxAction<{ groupId: number; releaseId: number }>) =>
    produce(state, draftState => {
      draftState.projectType = ProjectType.ReleaseGroup;
      draftState.projectContext[ProjectType.ReleaseGroup].groupId = action.groupId;
      draftState.projectContext[ProjectType.ReleaseGroup].releaseId = action.releaseId;
    }),
};

const setOptionReducer = {
  [actionTypes.SET_OPTIONS]:
  (state: ReportGenerationStore, action: SyncReduxAction<{ options: Partial<ReportOptions> }>) =>
    produce(state, draftState => {
      draftState.options = {
        ...draftState.options,
        ...action.options,
      };
    }),
};

const publicReportURLReducer = {
  [`${actionTypes.FETCH_PUBLIC_REPORT_URL}_PENDING`]: (state: ReportGenerationStore) =>
    produce(state, draftState => {
      draftState.asyncStatus.fetchPublicReportUrl = getPendingStatus();
    }),
  [`${actionTypes.FETCH_PUBLIC_REPORT_URL}_FULFILLED`]: (state: ReportGenerationStore, action: ReduxActionResponse<Report>) =>
    produce(state, draftState => {
      draftState.asyncStatus.fetchPublicReportUrl = getInitialStatus();
      draftState.publicReportUrl = action.payload.data?.publicURL ?? '';
    }),
  [`${actionTypes.FETCH_PUBLIC_REPORT_URL}_REJECTED`]: (state: ReportGenerationStore, action: ReduxActionResponse<Report>) =>
    produce(state, draftState => {
      draftState.asyncStatus.fetchPublicReportUrl = getErrorStatus(action.payload);
    }),
};

const queueEmailReportReducer = {
  [`${actionTypes.QUEUE_EMAIL_REPORT}_PENDING`]: (state: ReportGenerationStore) =>
    produce(state, draftState => {
      draftState.asyncStatus.queueEmailReport = getPendingStatus();
    }),
  [`${actionTypes.QUEUE_EMAIL_REPORT}_FULFILLED`]: (state: ReportGenerationStore) =>
    produce(state, draftState => {
      draftState.asyncStatus.queueEmailReport = getInitialStatus();
    }),
  [`${actionTypes.QUEUE_EMAIL_REPORT}_REJECTED`]: (state: ReportGenerationStore, action: ReduxActionResponse) =>
    produce(state, draftState => {
      draftState.asyncStatus.queueEmailReport = getErrorStatus(action.payload);
    }),
};

const queuePublishReportReducer = {
  [`${actionTypes.QUEUE_PUBLISH_REPORT}_PENDING`]: (state: ReportGenerationStore) =>
    produce(state, draftState => {
      draftState.asyncStatus.queuePublishReport = getPendingStatus();
    }),
  [`${actionTypes.QUEUE_PUBLISH_REPORT}_FULFILLED`]: (state: ReportGenerationStore) =>
    produce(state, draftState => {
      draftState.asyncStatus.queuePublishReport = getInitialStatus();
    }),
  [`${actionTypes.QUEUE_PUBLISH_REPORT}_REJECTED`]: (state: ReportGenerationStore, action: ReduxActionResponse) =>
    produce(state, draftState => {
      draftState.asyncStatus.queuePublishReport = getErrorStatus(action.payload);
    }),
};

const customHeaderReducer = {
  [actionTypes.SET_CUSTOM_HEADER_TEXT]:
  (state: ReportGenerationStore, action: SyncReduxAction<{ customHeaderText: string }>) =>
    produce(state, draftState => {
      draftState.customHeaderText = action.customHeaderText;
    }),
};

const dependencyInfoOptionsReducer = {
  [actionTypes.SET_DEPENDENCY_INFO_OPTIONS]:
    (state: ReportGenerationStore, action: SyncReduxAction<{ dependencyInfoOptions: DependencyInfoOption[] }>) =>
      produce(state, draftState => {
        draftState.dependencyInfoOptions = action.dependencyInfoOptions;
      }),
  [`${actionTypes.UPDATE_PROJECT_DEPENDENCY_INFO_OPTIONS}_PENDING`]: (state: ReportGenerationStore) =>
    produce(state, draftState => {
      draftState.asyncStatus.updateDependencyInfoOptions = getPendingStatus();
    }),
  [`${actionTypes.UPDATE_PROJECT_DEPENDENCY_INFO_OPTIONS}_FULFILLED`]: (state: ReportGenerationStore, action: ReduxActionResponse<Project>) =>
    produce(state, draftState => {
      draftState.asyncStatus.updateDependencyInfoOptions = getInitialStatus();
      draftState.dependencyInfoOptions = action.payload.data?.bom_column_settings as DependencyInfoOption[];
    }),
  [`${actionTypes.UPDATE_PROJECT_DEPENDENCY_INFO_OPTIONS}_REJECTED`]: (state: ReportGenerationStore, action: ReduxActionResponse) =>
    produce(state, draftState => {
      draftState.asyncStatus.updateDependencyInfoOptions = getErrorStatus(action.payload);
    }),
};

export default createReducer<ReportGenerationStore>(initialState, {
  ...resetStateReducer,
  ...setProjectTypeReducer,
  ...setProjectContextReducer,
  ...setCategoryReducer,
  ...setFormatReducer,
  ...setOptionReducer,
  ...publicReportURLReducer,
  ...queueEmailReportReducer,
  ...queuePublishReportReducer,
  ...customHeaderReducer,
  ...dependencyInfoOptionsReducer,
});
